import React from 'react';
import { createTheme, Grid, styled, ThemeProvider } from '@material-ui/core';
import { yellow, lightGreen, amber, red } from '@material-ui/core/colors';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import { PosPlugin } from 'plugins/plugin';
import {
  getCurrencyFormatter,
  getCurrencyFormatterNoSymbol,
  getDateFormatter,
  getNumberFormatFunction,
  getSetting,
} from 'reducers/configs/settings';
import { modalPages as mp } from 'constants/modalPage';
import { Expiration } from 'plugins/wbu/react';
import { getWbuExtraData } from 'plugins/wbu/redux';
import { openModalPage } from 'actions/modalPage';
import { getCustomerIssuedCoupons } from 'reducers/customerSearch';
import { getIsRetailView } from 'reducers/UI/gridDisplay';

import { generateExpirationValues } from '../utils';

import ExpandableCustomerBadgeRegion from './ExpandableCustomerBadgeRegion';

const Item = ({
  title,
  children,
  style,
}: {
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <>
    <span style={{ fontWeight: 'bold' }}>{title}</span>
    {children}
  </>
);

const overrideTheme = createTheme({
  palette: {
    secondary: amber,
    error: red,
  },
});

const WbuBadgeTheme = R.mergeDeepLeft({
  palette: {
    secondary: overrideTheme.palette.secondary,
    error: overrideTheme.palette.error,
  },
  overrides: overrideTheme.overrides,
});

const css = {
  Layout: styled(Grid)(({ mode }: { mode: 'retail' | 'normal' }) => ({
    fontSize: 14,
    display: 'grid',
    alignItems: 'center',
    gridTemplateAreas: [
      '"member member member member member"',
      '"savngs visits visits visits ______"',
      '"cemail ptsDSC pntsBB ptsCrd ______"',
      '"cbirth ptsDSC pntsBB ptsCrd ______"',
      '"cnotes cnotes cnotes cnotes cnotes"',
    ].join('\n'),
    gridTemplateColumns: {
      normal: 'fit-content(50ch) auto auto auto 1fr',
      retail: 'fit-content(50ch) auto auto auto 1fr',
    }[mode],
    gap: '0 1ch',
    '& > *': {
      textWrap: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })),
  areas: {
    Membership: styled(Grid)({ gridArea: 'member' }),
    Visits: styled(Grid)({ gridArea: 'visits' }),
    Savings: styled(Grid)({ gridArea: 'savngs' }),
    CustomerEmail: styled(Grid)({ gridArea: 'cemail' }),
    CustomerBirth: styled(Grid)({ gridArea: 'cbirth' }),
    PointsDSC: styled(Grid)({ gridArea: 'ptsDSC' }),
    PointsBB: styled(Grid)({ gridArea: 'pntsBB' }),
    PointsCredit: styled(Grid)({ gridArea: 'ptsCrd' }),
    Notes: styled(Grid)({ gridArea: 'cnotes', textWrap: 'wrap' }),
  },
  Color: styled(Grid)(
    ({ bg, type }: { bg?: string | undefined; type: 'pill' | 'plain' }) => {
      if (type === 'pill') {
        return {
          background: bg,
          // 100% would create an oval, because it would use a larger radius on the x axis
          // but 100vh is scaled down to make perfectly-circular end caps ᑕ____ᑐ
          borderRadius: '100vh',
          padding: '5px',
        };
      }
      return { background: bg };
    },
  ),
};

export const UICustomerBadgeCustomRegion: PosPlugin['UICustomerBadgeCustomRegion'] = ({
  currentCustomer,
  customerRewardPoints,
}) => {
  const dispatch = useDispatch();
  /* Formatters */
  const format = {
    currencyPlain: useSelector(getCurrencyFormatter),
    currencyWithSymbol: useSelector(getCurrencyFormatterNoSymbol).stringify,
    number: useSelector(getNumberFormatFunction),
    date: useSelector(getDateFormatter),
  };

  /* WBU specific APIs */
  const wbuExtraData = useSelector(getWbuExtraData);

  /* Expiration information to color the component */
  const {
    expiration,
    expired,
    expiresSoon,
    untilExpiration,
  } = generateExpirationValues(wbuExtraData?.dscExpiration);

  const hasAccurateCurrentSavings = useSelector(
    getSetting('WBUCustomerstatistics'),
  );
  const customerIssuedCoupons = useSelector(getCustomerIssuedCoupons);
  const unusedBB = customerIssuedCoupons.filter(cip => cip.couponCode === 'BB')
    .length;

  const totalVisits = format.number(Number(wbuExtraData?.total_visits || 0));
  const totalSales = format.currencyPlain(
    Number(wbuExtraData?.total_sales || 0).toFixed(0),
    {
      preserveInteger: true,
    },
  );
  const isRetailView = useSelector(getIsRetailView);
  return (
    <ThemeProvider theme={WbuBadgeTheme}>
      <ExpandableCustomerBadgeRegion
        minHeight={170}
        maxHeight={500}
        loading={!wbuExtraData}
        onClick={() => {
          dispatch(
            openModalPage({
              component: mp.customerView,
              // @ts-ignore
              groupID: mp.customerView,
              replace: true,
            }),
          );
        }}
      >
        <css.Layout
          mode={isRetailView ? 'retail' : 'normal'}
          // No key causes a weird bug where the grid fails to update its layout
          // to match the new styles. By observing the styles in the devtools,
          // one can see that the styles are getting correctly applied. Thus
          // ruling out an issue in our application code or react
          // To repro: Without a key prop, toggle retail and grid views a lot,
          // it usually gets stuck in the retail layout
          key={`layout-${isRetailView}`}
        >
          <css.areas.Membership>
            {(function getMembershipPill() {
              const start = format.date(
                wbuExtraData?.currentActivation ?? wbuExtraData?.startDateTime,
              );
              const end = format.date(expiration);
              if (expiration && expired)
                return (
                  <css.Color type="pill" bg={red[400]}>
                    Membership expired on {end}
                  </css.Color>
                );
              if (expiresSoon)
                return (
                  <css.Color type="pill" bg={amber[500]}>
                    Active member {start} - {end} ({untilExpiration} days)
                  </css.Color>
                );
              if (expiration)
                return (
                  <css.Color type="pill" bg={lightGreen[500]}>
                    Active member {start} - {end} ({untilExpiration} days)
                  </css.Color>
                );
              return (
                <css.Color type="pill" bg={red[400]}>
                  Non-member
                </css.Color>
              );
            })()}
          </css.areas.Membership>
          <css.areas.Savings>
            {(function getSavingsInfo() {
              if (!wbuExtraData?.dscExpiration) {
                const savings = format.currencyPlain(
                  wbuExtraData?.potential_savings ?? 0,
                );
                return <Item title="Potential savings: ">{savings}</Item>;
              }
              if (!hasAccurateCurrentSavings) return null;
              const savings = format.currencyPlain(
                wbuExtraData?.dsc_current_savings ?? 0,
              );
              return <Item title="Current savings: ">{savings}</Item>;
            })()}
          </css.areas.Savings>
          <css.areas.Visits>
            <Item title="Visits: ">
              {totalVisits ? `${totalVisits} / ${totalSales}` : '0'}
            </Item>
          </css.areas.Visits>

          <css.areas.CustomerEmail>
            <Item title="Email: ">{currentCustomer?.email}</Item>
          </css.areas.CustomerEmail>
          <css.areas.CustomerBirth>
            <Item title="Birth: ">{currentCustomer?.householdCode}</Item>
          </css.areas.CustomerBirth>

          {expiration ? (
            <css.areas.PointsDSC>
              <Item title="DSC Pts">
                <div>{format.currencyWithSymbol(customerRewardPoints)}</div>
              </Item>
            </css.areas.PointsDSC>
          ) : null}
          {expiration ? (
            <css.areas.PointsBB>
              <Item title="Unused BB">
                <div>{unusedBB}</div>
              </Item>
            </css.areas.PointsBB>
          ) : null}
          <css.areas.PointsCredit>
            <css.Color
              type="plain"
              bg={
                (currentCustomer.availableCredit ?? 0) > 0
                  ? yellow[500]
                  : undefined
              }
            >
              <Item title="Credit">
                <div>
                  {format.currencyPlain(currentCustomer.availableCredit ?? 0)}
                </div>
              </Item>
            </css.Color>
          </css.areas.PointsCredit>

          <css.areas.Notes>
            <Item title="Note: ">{currentCustomer.notes ?? ''}</Item>
          </css.areas.Notes>
        </css.Layout>
      </ExpandableCustomerBadgeRegion>
    </ThemeProvider>
  );
};
